// admin-ticket-analytics-dashboard S2 — Per-location Analytics page (MVP).
// admin-analytics-expansion P1 (D2) — Tabs layout: "Location" (unchanged,
// supervisor + org_admin) and "Organization" (org_admin only).
//
// Live KPI cards for a single location, date-range filtered (default last
// 30 days). Role guard (org_admin + supervisor) is applied by the route
// wrapper; this component itself uses `getMyLocations()` (not
// `listLocations()`) for the location switcher, because `listLocations()`
// is org_admin-only server-side (see admin-locations.controller.ts
// requireOrgAdminActor) while `getMyLocations()` (`GET /me/locations`)
// correctly resolves org-wide locations for org_admin and assigned-only
// locations for supervisor — mirroring the AppShell supervisor picker
// convention and R4 role scoping from the backend guard.
//
// NOTE: `AnalyticsKpi` (backend S1) does not carry a currency code, so
// revenue is formatted with a fixed USD fallback for this MVP slice —
// flagged as a known gap, not blocking (see apply-progress deviations).
//
// The Organization tab (P1) is entirely absent for supervisor — mirroring
// the backend's least-privilege guard (`getOrgAnalytics` returns 403 for
// supervisor) — and its `getOrgAnalytics` query is additionally gated with
// `enabled: role === 'org_admin'` as a belt-and-suspenders safeguard so the
// request is never issued for a role that would just get rejected.

import { getLocationAnalytics, getMyLocations, getOrgAnalytics } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import { DEFAULT_CURRENCY, type LocationAnalyticsResponse } from '@jq4race/shared';
import {
  formatAvgSeconds,
  formatCents,
  Input,
  Skeleton,
  StatCard,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@jq4race/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Car, KeyRound, TriangleAlert, Users as UsersIcon } from 'lucide-react';
import { useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import {
  gatherLocationReport,
  gatherOrgReport,
} from '../../application/reports/gather-report-data';
import { DistributionBar } from '../components/DistributionBar';
import { ExportPdfButton } from '../components/ExportPdfButton';
import { LocationBreakdownTable } from '../components/LocationBreakdownTable';
import { OrgRollup } from '../components/OrgRollup';
import { TrendsSection } from '../components/TrendsSection';
import { toFilenameSlug } from '../pdf/downloadReport';

const CURRENCY_FALLBACK = DEFAULT_CURRENCY;
const DEFAULT_RANGE_DAYS = 30;

function toDateInputValue(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - DEFAULT_RANGE_DAYS);
  return { from: toDateInputValue(from), to: toDateInputValue(to) };
}

export interface AnalyticsPageProps {
  /**
   * Determines whether the Organization tab is offered (org_admin only).
   * Named `actorRole` (not `role`) to avoid colliding with the JSX/ARIA
   * `role` attribute name (biome's `useValidAriaRole` lint rule flags any
   * `role="..."` JSX attribute regardless of element). Typed as `string`
   * (not the shared `Role` union) to match `Session.user.role`'s own type,
   * mirroring the route wrapper's `ALLOWED_ROLES.includes(role)`
   * string-comparison convention.
   */
  actorRole?: string;
}

export function AnalyticsPage({ actorRole }: AnalyticsPageProps) {
  const { t } = useTranslation('analytics');
  const isOrgAdmin = actorRole === 'org_admin';

  const [range, setRange] = useState(defaultRange);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | undefined>(undefined);

  // ── Location list (role-scoped: org-wide for org_admin, assigned-only for
  // supervisor) ─────────────────────────────────────────────────────────────
  const {
    data: locationsData,
    isLoading: locationsLoading,
    isError: locationsError,
  } = useQuery({
    queryKey: ['my-locations'],
    queryFn: () => getMyLocations(),
  });

  const activeLocations = (locationsData?.locations ?? []).filter((l) => l.status === 'active');
  const sortedLocations = [...activeLocations].sort((a, b) => a.name.localeCompare(b.name));
  const firstLocationId = sortedLocations[0]?.id;
  const effectiveLocationId = selectedLocationId ?? firstLocationId;

  // Client-side locationId -> name join (D3) — reuses the already-fetched
  // `getMyLocations()` response, so the Organization tab's breakdown table
  // never triggers an extra request. `getMyLocations()` only returns active
  // locations, so any breakdown row whose id is absent (e.g. a hard-deleted
  // location still referenced by historical tickets) falls back to an em dash.
  const locationNameById = new Map(
    (locationsData?.locations ?? []).map((loc) => [loc.id, loc.name]),
  );

  // ── KPI query (Location tab) ────────────────────────────────────────────
  const {
    data: kpis,
    isLoading: kpisLoading,
    isFetching: kpisFetching,
    isError: kpisError,
    error: kpisErrorObj,
  } = useQuery({
    queryKey: ['location-analytics', effectiveLocationId, range.from, range.to],
    queryFn: () =>
      getLocationAnalytics(effectiveLocationId as string, {
        from: `${range.from}T00:00:00.000Z`,
        to: `${range.to}T23:59:59.999Z`,
      }),
    enabled: !!effectiveLocationId,
    placeholderData: keepPreviousData,
  });

  // ── Org rollup query (Organization tab, org_admin only) ──────────────────
  // `enabled: isOrgAdmin` is a belt-and-suspenders safeguard: the tab itself
  // is never rendered for supervisor, so this query would already never
  // mount for them, but the explicit gate keeps the P1 spec's "never call
  // getOrgAnalytics for supervisor" guarantee independent of the tab UI.
  const {
    data: orgKpis,
    isError: orgError,
    error: orgErrorObj,
  } = useQuery({
    queryKey: ['org-analytics', range.from, range.to],
    queryFn: () =>
      getOrgAnalytics({
        from: `${range.from}T00:00:00.000Z`,
        to: `${range.to}T23:59:59.999Z`,
      }),
    enabled: isOrgAdmin,
    placeholderData: keepPreviousData,
  });

  function commitRange(next: { from: string; to: string }) {
    setRangeError(null);
    if (next.from && next.to && new Date(next.from) > new Date(next.to)) {
      setRangeError(t('filters.rangeError'));
      return;
    }
    setRange(next);
  }

  const showKpiSkeleton = !kpis;
  const showUpdating = kpisFetching && !kpisLoading;

  // ── PDF export handlers ──────────────────────────────────────────────────
  // Each export re-fetches its inputs fresh (gather*Report) then lazy-imports
  // the PDF module so @react-pdf/renderer never lands in the route's initial
  // chunk. `new Date()` here just stamps the report's "Generated" line.
  async function handleExportLocation() {
    if (!effectiveLocationId) return;
    const locationName =
      locationNameById.get(effectiveLocationId) ?? t('filters.locationFallbackName');
    const data = await gatherLocationReport({
      locationId: effectiveLocationId,
      locationName,
      from: `${range.from}T00:00:00.000Z`,
      to: `${range.to}T23:59:59.999Z`,
    });
    const { exportLocationReport } = await import('../pdf/export-reports');
    await exportLocationReport(
      data,
      new Date().toISOString(),
      `analytics-${toFilenameSlug(locationName)}-${range.from}_${range.to}.pdf`,
    );
  }

  async function handleExportOrg() {
    const data = await gatherOrgReport({
      from: `${range.from}T00:00:00.000Z`,
      to: `${range.to}T23:59:59.999Z`,
      locationNames: Object.fromEntries(locationNameById),
    });
    const { exportOrgReport } = await import('../pdf/export-reports');
    await exportOrgReport(
      data,
      new Date().toISOString(),
      `analytics-organization-${range.from}_${range.to}.pdf`,
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
      />

      <Tabs defaultValue="location">
        <TabsList>
          <TabsTrigger value="location">{t('tabs.location')}</TabsTrigger>
          {isOrgAdmin && <TabsTrigger value="organization">{t('tabs.organization')}</TabsTrigger>}
        </TabsList>

        <TabsContent value="location" className="space-y-8">
          {/* Filters: location switcher + date range */}
          <div className="flex flex-wrap items-end gap-3">
            {locationsLoading ? (
              <Skeleton className="h-9 w-48" />
            ) : locationsError ? (
              <span className="text-[12px] text-jq-danger">{t('filters.locationsLoadError')}</span>
            ) : sortedLocations.length === 0 ? null : (
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="analytics-location"
                  className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite"
                >
                  {t('filters.locationLabel')}
                </label>
                <select
                  id="analytics-location"
                  value={effectiveLocationId ?? ''}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="h-9 rounded-md border border-jq-sand/70 bg-jq-surface-0 px-3 text-[13px] text-jq-charcoal focus:border-jq-navy/40 focus:outline-none focus:ring-1 focus:ring-jq-navy/20"
                >
                  {sortedLocations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-col gap-1">
              <label
                htmlFor="analytics-from"
                className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite"
              >
                {t('filters.fromLabel')}
              </label>
              <Input
                id="analytics-from"
                type="date"
                value={range.from}
                onChange={(e) => commitRange({ ...range, from: e.target.value })}
                className="h-9 w-[160px] text-[13px]"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="analytics-to"
                className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite"
              >
                {t('filters.toLabel')}
              </label>
              <Input
                id="analytics-to"
                type="date"
                value={range.to}
                onChange={(e) => commitRange({ ...range, to: e.target.value })}
                className="h-9 w-[160px] text-[13px]"
              />
            </div>

            {showUpdating && (
              <span className="flex items-center pb-2 text-[12px] text-jq-graphite">
                {t('filters.updating')}
              </span>
            )}

            <div className="ml-auto">
              <ExportPdfButton
                onExport={handleExportLocation}
                disabled={!effectiveLocationId || !kpis || kpisError}
              />
            </div>
          </div>

          {rangeError && (
            <p role="alert" className="text-[12px] text-jq-danger">
              {rangeError}
            </p>
          )}

          {/* Empty locations state */}
          {!locationsLoading && !locationsError && sortedLocations.length === 0 ? (
            <NoLocationsState />
          ) : kpisError ? (
            <KpiErrorState error={kpisErrorObj} />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard
                icon={UsersIcon}
                label={t('kpi.volume')}
                value={
                  showKpiSkeleton ? null : (kpis as LocationAnalyticsResponse).volume.toString()
                }
              />
              <StatCard
                icon={KeyRound}
                label={t('kpi.revenue')}
                value={
                  showKpiSkeleton
                    ? null
                    : formatCents(
                        (kpis as LocationAnalyticsResponse).netRevenueCents,
                        CURRENCY_FALLBACK,
                      )
                }
              />
              <StatCard
                icon={Car}
                label={t('kpi.avgRetrievalTime')}
                value={
                  showKpiSkeleton
                    ? null
                    : (kpis as LocationAnalyticsResponse).avgRetrievalSeconds !== null
                      ? formatAvgSeconds(
                          (kpis as LocationAnalyticsResponse).avgRetrievalSeconds as number,
                        )
                      : '—'
                }
              />
            </div>
          )}

          {/* Trend charts (P4b): only once a location is resolved and the KPI
              cards above rendered successfully — mirrors the same guard. */}
          {!locationsLoading &&
            !locationsError &&
            sortedLocations.length > 0 &&
            !kpisError &&
            effectiveLocationId && (
              <TrendsSection
                locationId={effectiveLocationId}
                from={`${range.from}T00:00:00.000Z`}
                to={`${range.to}T23:59:59.999Z`}
              />
            )}
        </TabsContent>

        {isOrgAdmin && (
          <TabsContent value="organization" className="space-y-8">
            {orgError ? (
              <KpiErrorState error={orgErrorObj} />
            ) : (
              <>
                <div className="flex justify-end">
                  <ExportPdfButton onExport={handleExportOrg} disabled={!orgKpis} />
                </div>
                <OrgRollup total={orgKpis?.total} />
                <LocationBreakdownTable
                  rows={orgKpis?.byLocation}
                  locationNameById={locationNameById}
                />
                <DistributionBar total={orgKpis?.total} />
                {/* Org-wide trends (P4b) — no locationId (UTC bucketing).
                    `enabled: isOrgAdmin` is the same belt-and-suspenders
                    safeguard as `getOrgAnalytics` above: the tab itself is
                    never rendered for supervisor, so this never mounts for
                    them, but the explicit gate keeps the guarantee
                    independent of the tab UI. */}
                <TrendsSection
                  from={`${range.from}T00:00:00.000Z`}
                  to={`${range.to}T23:59:59.999Z`}
                  enabled={isOrgAdmin}
                />
              </>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function NoLocationsState() {
  const { t } = useTranslation('analytics');
  return (
    <div className="rounded-sm border border-jq-sand/60 bg-jq-surface-0 px-6 py-10 text-center">
      <p className="text-[14px] text-jq-graphite">{t('emptyStates.noLocationsTitle')}</p>
      <p className="mt-1 text-[13px] text-jq-stone">{t('emptyStates.noLocationsDescription')}</p>
    </div>
  );
}

function KpiErrorState({ error }: { error: Error | null }) {
  const { t } = useTranslation('analytics');
  const message = translateApiError(error, t, { fallback: t('errors.analyticsLoadError') });
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-4 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="leading-snug">{message}</p>
    </div>
  );
}
